import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { stringify } from 'qs';

import {
  AXIOS_DEFAULT_CONFIG,
  Web3Address,
  createTOTPHeaders,
  BlockchainID,
} from '../common';
import {
  BundlePaymentPlan,
  GetMyBundlesStatusResponse,
  GetLinkForBundlePaymentRequest,
} from './bundles';
import {
  IGetLinkForCardPaymentRequest,
  IGetSubscriptionPricesResponse,
  IGetLinkForCardPaymentResponse,
  ISubscriptionsResponse,
  IApiCancelSubscriptionRequestParams,
  IGetLinkForRecurrentCardPaymentRequest,
  ICanPayByCardResponse,
} from './subscriptions';
import {
  IApiUserGroupParams,
  IUserGroupsResponse,
  IApiUserGroupDetails,
  IGetGroupJwtRequestParams,
  IGetGroupJwtResponse,
  ICreateUserGroupParams,
  ICreateUserGroupResponse,
  IGetIsGroupCreationAvailableResponse,
} from './userGroup';
import {
  IUpdateWhitelistModeParams,
  IGetWhitelistParams,
  IUpdateWhitelistModeRequestParams,
  IUpdateWhitelistModeResponse,
  IUpdateWhitelistParams,
  IUpdateWhitelistParamsResponse,
  IGetWhitelistParamsResponse,
  IWhitelistBlockchainsParams,
  ReplaceWhitelistParams,
  ReplaceWhitelistBody,
  ReplaceWhitelistResponse,
} from './whitelists';
import {
  AcceptToSParams,
  GetToSStatusParams,
  GetToSStatusResponse,
} from './tos';
import {
  ConfirmTFAParams,
  ConfirmTFAResponse,
  DisableTFAResponse,
  InitTFAResponse,
  TFAStatusResponse,
} from './totp';
import {
  ICheckInstantJwtParticipantResponse,
  IGetOrCreateInstantJwt,
} from './devDAO';
import {
  IJwtTokenRequestParams,
  IJwtTokenLimitResponse,
  IJwtTokenResponse,
  IJwtTokenCreateParams,
  IUpdateJwtTokenFreezeStatusParams,
  IUpdateJwtTokenFreezeStatusRequestParams,
  IJwtTokenRequestBody,
  GetUserEndpointTokenStatusRequest,
  GetUserEndpointTokenStatusResponse,
} from './JWT';
import { IDailyChargingParams, IDailyChargingResponse } from './telemetry';
import {
  IActiveBoundAccount,
  IBindEmailParams,
  IBoundEmail,
  IEditBoundEmailParams,
  IDeleteBoundEmailParams,
  IGetBoundEmailsParams,
  IGetBoundEmailsResponse,
} from './email';
import {
  IAggregatedPaymentHistoryRequest,
  IAggregatedPaymentHistoryResponse,
  IMyBundlesPaymentsRequest,
  IMyBundlesPaymentsResponse,
  IPaymentHistoryRequest,
  IPaymentHistoryResponse,
} from './transactionsHistory';
import { IBalance } from './balance';
import {
  IGoogleLoginParamsResponse,
  IOauthLoginResponse,
  IETHAddressesResponse,
  IDecodeJwtTokenParams,
  IDecodeJwtTokenResponse,
  IGoogleSecretCodeParams,
  ISyntheticJwtTokenResponse,
  IUnbindOauthAccountResponse,
} from './googleOauth';
import {
  IOauthLoginParams,
  ISecretCodeLoginQueryParams,
  IOauthSecretCodeParams,
  AssociatedAccount,
} from './oauth';
import {
  AcceptGroupInvitationParams,
  ICancelGroupInvitationParams,
  IManageGroupInvitationResponse,
  IDeleteGroupMemberParams,
  ILeaveGroupResponse,
  IRenameGroupParams,
  IRenameGroupResponse,
  IUpdateGroupMemberRoleParams,
  IUpdateGroupMemberRoleResponse,
  InivteGroupMemberResponse,
  InviteGroupMemeberParams,
  RejectGroupInvitationParams,
  IGetGroupInvitationsResponse,
  IGetGroupInvitationsParams,
} from './groups';
import {
  IApplyReferralCodeParams,
  IApplyReferralCodeResult,
  IConvertReferralRewardParams,
  ICreateReferralCodeParams,
  ICreateReferralCodeResult,
  IGetReferralCodesParams,
  IGetReferralCodesResult,
  IGetReferralLinkByCodesResult,
  IGetReferralLinksByCodesParams,
  IGetReferralsCountParams,
  IGetReferralsCountResult,
  IGetReferrerParams,
  IGetReferrerResponse,
  IGetRewardBalanceParams,
  IGetRewardBalanceResult,
  IGetRewardTxsParams,
  IGetRewardTxsResponse,
} from './referralProgram';
import {
  IGetCryptoPaymentOptionsParams,
  IGetCryptoPaymentOptionsResponse,
} from './payments';
import {
  IGetNotificationsResponse,
  IGetNotificationsParams,
  IMarkNotificationsAsReadResponse,
  IMarkNotificationsAsReadParams,
  IGetNotificationsChannelsResponse,
  IGetNotificationsChannelsParams,
  IGetMessengerNotificationsBotDataResponse,
  IEnableMessengerIntegrationResponse,
  IEnableTelegramIntegrationParams,
  IDeleteNotificationsChannelParams,
  IDeleteNotificationsChannelResponse,
  IUpdateNotificationsChannelParams,
  INotificationsChannelConfig,
  IEnableSlackIntegrationParams,
  IAddNewEmailForNotificationsParams,
  IConfirmEmailForNotificationsParams,
  IGetMessengerNotificationsBotDetailsResponse,
  IGetMessengerNotificationsBotDetailsParams,
  IUpdateNotificationsChannelStatusParams,
  IUpdateNotificationsChannelStatusResponse,
} from './notifications';
import {
  ICheckOrGenerateDepositInvoiceResponse,
  IGetDepositInvoiceParams,
  IGetStripeInvoiceDocumentParams,
  IGetStripeInvoiceDocumentResponse,
  TCheckOrGenerateDepositInvoiceParams,
  TGetDepositInvoiceResponse,
} from './invoices';
import {
  APIKey,
  CreateAPIKeyParams,
  CreateAPIKeyResponse,
  DeleteAPIKeysParams,
  DeleteAPIKeysResponse,
} from './APIKey';
import {
  IGetUIProfileConfigParams,
  IUIProfileConfig,
  IUpdateUIProfileConfigParams,
  IUpdateUIProfileConfigResponse,
} from './configs';
import {
  ICreateNewSessionParams,
  ICreateNewSessionResponse,
  IDeleteSessionParams,
  IDeleteSessionResponse,
  IGetAllSessionsResponse,
  ILogoutCurrentSessionResponse,
} from './sessions';

export class AccountingGateway {
  public api: AxiosInstance;

  constructor(private readonly config: AxiosRequestConfig) {
    this.api = axios.create({ ...config, ...AXIOS_DEFAULT_CONFIG });
  }

  addToken(token: string) {
    this.api = axios.create({
      ...this.config,
      ...AXIOS_DEFAULT_CONFIG,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  removeToken() {
    this.api = axios.create({ ...this.config, ...AXIOS_DEFAULT_CONFIG });
  }

  async getAnkrBalance(params: IApiUserGroupParams) {
    const { data: response } = await this.api.get<IBalance>(
      '/api/v1/auth/balance',
      { params },
    );

    return response;
  }

  async getPaymentHistory(params: IPaymentHistoryRequest) {
    const { data: response } = await this.api.get<IPaymentHistoryResponse>(
      '/api/v1/auth/transactionHistory',
      {
        params,
        paramsSerializer: request => stringify(request, { indices: false }),
      },
    );

    return response;
  }

  async getAggregatedPaymentHistory(params: IAggregatedPaymentHistoryRequest) {
    const { data: response } =
      await this.api.get<IAggregatedPaymentHistoryResponse>(
        '/api/v1/auth/aggregatedTransactions',
        {
          params,
          paramsSerializer: request => stringify(request, { indices: false }),
        },
      );

    return response;
  }

  async getMyBundlesPaymentsHistory(params: IMyBundlesPaymentsRequest) {
    const { data: response } = await this.api.get<IMyBundlesPaymentsResponse>(
      '/api/v1/auth/myBundles/payments',
      {
        params,
        paramsSerializer: (request: IMyBundlesPaymentsRequest) =>
          stringify(request, { indices: false }),
      },
    );

    return response;
  }

  public async getSubscriptions(params: IApiUserGroupParams) {
    const { data: response } = await this.api.get<ISubscriptionsResponse>(
      '/api/v1/auth/payment/getMySubscriptions',
      { params },
    );

    return response;
  }

  async cancelSubscription(
    body: IApiCancelSubscriptionRequestParams,
    { totp, ...params }: IApiUserGroupParams,
  ) {
    await this.api.post<void>('/api/v1/auth/payment/cancelSubscription', body, {
      headers: createTOTPHeaders(totp),
      params,
    });
  }

  async getDailyCharging(params: IDailyChargingParams) {
    const { data: response } = await this.api.get<IDailyChargingResponse>(
      '/api/v1/auth/oneDayUsageReport',
      {
        params,
      },
    );

    return response;
  }

  // returns all bounded emails list with related statuses
  async getBoundEmails(params: IGetBoundEmailsParams | void) {
    const {
      data: { bindings },
    } = await this.api.get<IGetBoundEmailsResponse>('/api/v1/auth/email', {
      params,
    });

    return bindings;
  }

  // Method returns active email for current user
  // use list instead (getBoundEmails)
  async getActiveBoundEmail() {
    const { data: response } = await this.api.get<IActiveBoundAccount>(
      '/api/v1/auth/email/active',
    );

    return response;
  }

  // bind/edit provider: email for this account
  async bindEmail({ email, totp }: IBindEmailParams) {
    const { data: response } = await this.api.post<IBoundEmail>(
      '/api/v1/auth/email/bind',
      null,
      {
        params: { email },
        headers: createTOTPHeaders(totp),
      },
    );

    return response;
  }

  async editBoundEmail({ email, totp }: IEditBoundEmailParams) {
    const { data: response } = await this.api.patch<IBoundEmail>(
      '/api/v1/auth/email/bind',
      null,
      {
        params: { email },
        headers: createTOTPHeaders(totp),
      },
    );

    return response;
  }

  async deleteBoundEmail({ email, totp }: IDeleteBoundEmailParams) {
    const { data: response } = await this.api.delete<IBoundEmail>(
      '/api/v1/auth/email/bind',
      {
        params: { email },
        headers: createTOTPHeaders(totp),
      },
    );

    return response;
  }

  async confirmEmailBinding(email: string, code: string) {
    const { data: response } = await this.api.post<IBoundEmail>(
      '/api/v1/auth/email/confirm',
      null,
      {
        params: { email, code },
      },
    );

    return response;
  }

  async resendConfirmationCode(email: string) {
    await this.api.post<string>('/api/v1/auth/email/resendConfirmation', null, {
      params: { email },
    });
  }

  async canPayByCard(params: IApiUserGroupParams) {
    const { data: response } = await this.api.get<ICanPayByCardResponse>(
      '/api/v1/auth/payment/isEligibleForCardPayment',
      { params },
    );

    return response;
  }

  async getLinkForCardPayment(
    body: IGetLinkForCardPaymentRequest,
    params: IApiUserGroupParams,
  ) {
    const { data: response } =
      await this.api.post<IGetLinkForCardPaymentResponse>(
        '/api/v1/auth/payment/depositWithCard',
        body,
        { params },
      );

    return response;
  }

  async getLinkForRecurrentCardPayment(
    body: IGetLinkForRecurrentCardPaymentRequest,
    params: IApiUserGroupParams,
  ) {
    const { data: response } =
      await this.api.post<IGetLinkForCardPaymentResponse>(
        '/api/v1/auth/payment/subscribeOnRecurrentPayments',
        body,
        { params },
      );

    return response;
  }

  async getUSDSubscriptionPrices(params: IApiUserGroupParams) {
    const { data: response } =
      await this.api.get<IGetSubscriptionPricesResponse>(
        '/api/v1/auth/payment/getSubscriptionPrices',
        { params },
      );

    return response;
  }

  async checkDevdaoInstantJwtParticipant() {
    const { data: response } =
      await this.api.get<ICheckInstantJwtParticipantResponse>(
        '/api/v1/auth/devdao/isParticipant',
      );

    return response;
  }

  async getOrCreateDevdaoInstantJwt(totp?: string) {
    const { data: response } = await this.api.get<IGetOrCreateInstantJwt>(
      '/api/v1/auth/devdao/getOrCreateJwtForUserAddress',
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return response;
  }

  async getOrCreateInstantJwt(totp?: string) {
    const { data: response } = await this.api.get<IGetOrCreateInstantJwt>(
      '/api/v1/auth/getOrCreateJwtForUserAddress',
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return response;
  }

  async getAllowedJwtTokensCount(params: IApiUserGroupParams): Promise<number> {
    const { data } = await this.api.get<IJwtTokenLimitResponse>(
      '/api/v1/auth/jwt/allowedCount',
      { params },
    );

    return data.jwtLimit;
  }

  async getAllJwtToken(params: IApiUserGroupParams) {
    const { data } = await this.api.get<IJwtTokenResponse[]>(
      '/api/v1/auth/jwt/all',
      { params },
    );

    return data;
  }

  async createJwtToken(
    body: IJwtTokenRequestBody,
    params: IJwtTokenCreateParams,
  ) {
    const { data } = await this.api.post<IJwtTokenResponse>(
      `/api/v1/auth/jwt/additional`,
      body,
      { params },
    );

    return data;
  }

  async updateJwtToken(
    body: IJwtTokenRequestBody,
    params: IJwtTokenRequestParams,
  ) {
    await this.api.patch('/api/v1/auth/jwt/additional', body, { params });
  }

  async updateJwtTokenFreezeStatus(
    params: IUpdateJwtTokenFreezeStatusParams,
    bodyParams: IUpdateJwtTokenFreezeStatusRequestParams,
  ) {
    const { data } = await this.api.patch<null>(
      '/api/v1/auth/jwt/additional/freeze',
      bodyParams,
      { params },
    );

    return data;
  }

  async getUserEndpointTokenStatus(params: GetUserEndpointTokenStatusRequest) {
    const { data } = await this.api.get<GetUserEndpointTokenStatusResponse>(
      `/api/v1/auth/jwt/additional/status`,
      { params },
    );

    return data;
  }

  async deleteJwtToken({ totp, ...params }: IJwtTokenRequestParams) {
    const { data: response } = await this.api.delete(`/api/v1/auth/jwt`, {
      headers: createTOTPHeaders(totp),
      params,
    });

    return response;
  }

  async getGroupJwtToken(params: IGetGroupJwtRequestParams) {
    const { data } = await this.api.get<IGetGroupJwtResponse>(
      '/api/v1/auth/group/jwt',
      { params },
    );

    return data;
  }

  async getUserGroups() {
    const { data: response } = await this.api.get<IUserGroupsResponse>(
      '/api/v1/auth/group',
    );

    return response;
  }

  async getUserGroupDetails(params: IApiUserGroupParams) {
    const { data: response } = await this.api.get<IApiUserGroupDetails>(
      '/api/v1/auth/groups/details',
      { params },
    );

    return response;
  }

  /**
  request fields description:

  name (optional): human readable name of the group

  company_type (optional): human readable information about group type (or company)

  comment (optional): any comment owner is willing to provide, ASCII only, up to 254 symbols

  transfer_assets (optional): should be set to trueif user is willing to transfer ALL his own assets to the group

  IMPORTANT:

  transfer_assets is NOT applicable to Metamask users

  Please logout user (delete access token) and ask user to log in again if transfer_assets is set to true and asset_transfer_done is true in response
  */

  async createUserGroup(body: ICreateUserGroupParams) {
    const { data: response } = await this.api.post<ICreateUserGroupResponse>(
      '/api/v1/auth/groups/new',
      body,
    );

    return response;
  }

  async getIsGroupCreationAvailable() {
    const { data: response } =
      await this.api.get<IGetIsGroupCreationAvailableResponse>(
        '/api/v1/auth/groups/new/isAllowed',
      );

    return response;
  }

  async initTFA() {
    const { data: response } = await this.api.post<InitTFAResponse>(
      '/api/v1/auth/2fa/init',
    );

    return response;
  }

  async getTFAStatus() {
    const { data: response } = await this.api.get<TFAStatusResponse>(
      '/api/v1/auth/2fa/status',
    );

    return response;
  }

  async confirmTFA(body: ConfirmTFAParams) {
    const { data: response } = await this.api.post<ConfirmTFAResponse>(
      '/api/v1/auth/2fa/confirm',
      body,
    );

    return response;
  }

  async disableTFA(totp: string) {
    const { data: response } = await this.api.post<DisableTFAResponse>(
      '/api/v1/auth/2fa/clear',
      null,
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return response;
  }

  async getToSStatus(params?: GetToSStatusParams) {
    const { data: response } = await this.api.get<GetToSStatusResponse>(
      '/api/v1/auth/tos/status',
      { params },
    );

    return response;
  }

  async acceptToS(params?: AcceptToSParams) {
    await this.api.post(
      '/api/v1/auth/tos/accept',
      { tosAccepted: true },
      { params },
    );
  }

  async getWhitelist(queryParams: IGetWhitelistParams) {
    const { data } = await this.api.get<IGetWhitelistParamsResponse>(
      '/api/v1/auth/whitelist',
      { params: queryParams },
    );

    return data;
  }

  async getBundlePaymentPlans() {
    const { data } = await this.api.get<BundlePaymentPlan[]>(
      '/api/v1/auth/bundles',
      { params: { activeOnly: false } },
    );

    return data;
  }

  async getLinkForBundlePayment(
    body: GetLinkForBundlePaymentRequest,
    params: IApiUserGroupParams,
  ) {
    const { data } = await this.api.post<IGetLinkForCardPaymentResponse>(
      '/api/v1/auth/myBundles/subscribe',
      body,
      { params },
    );

    return data;
  }

  async getMyBundles(group?: Web3Address) {
    const { data } = await this.api.get<ISubscriptionsResponse>(
      '/api/v1/auth/myBundles',
      { params: { group } },
    );

    return data.items;
  }

  async cancelBundleSubscription(
    subscription_id: string,
    { totp, ...params }: IApiUserGroupParams,
  ) {
    const { data } = await this.api.post(
      '/api/v1/auth/myBundles/unsubscribe',
      { subscription_id },
      { headers: createTOTPHeaders(totp), params },
    );

    return data;
  }

  async getMyBundlesStatus(group?: Web3Address) {
    const {
      data: { bundles },
    } = await this.api.get<GetMyBundlesStatusResponse>(
      '/api/v1/auth/myBundles/status',
      { params: { group } },
    );

    return bundles;
  }

  async updateWhitelistMode(
    bodyParams: IUpdateWhitelistModeRequestParams,
    queryParams: IUpdateWhitelistModeParams,
    totp?: string,
  ) {
    const { data } = await this.api.patch<IUpdateWhitelistModeResponse>(
      '/api/v1/auth/whitelist/mode',
      bodyParams,
      { headers: createTOTPHeaders(totp), params: queryParams },
    );

    return data;
  }

  async addAddressToWhitelist(
    item: string,
    queryParams: IUpdateWhitelistParams,
    totp?: string,
  ) {
    const { data } = await this.api.post<IUpdateWhitelistParamsResponse>(
      '/api/v1/auth/whitelist',
      { item },
      { headers: createTOTPHeaders(totp), params: queryParams },
    );

    return data;
  }

  async updateWhitelist(
    bodyParams: string[],
    queryParams: IUpdateWhitelistParams,
    totp?: string,
  ) {
    const { data } = await this.api.patch<IUpdateWhitelistParamsResponse>(
      '/api/v1/auth/whitelist',
      bodyParams,
      { headers: createTOTPHeaders(totp), params: queryParams },
    );

    return data;
  }

  async getWhitelistBlockchains(queryParams: IWhitelistBlockchainsParams) {
    const { data } = await this.api.get<BlockchainID[]>(
      '/api/v1/auth/whitelist/blockchains',
      { params: queryParams },
    );

    // empty array means all blockchains are included to project
    // ['*'] also means all blockchains are included to project
    return data;
  }

  async addBlockchainsToWhitelist(
    bodyParams: BlockchainID[],
    queryParams: IWhitelistBlockchainsParams,
    totp?: string,
  ) {
    const { data } = await this.api.post<BlockchainID[]>(
      '/api/v1/auth/whitelist/blockchains',
      bodyParams,
      { headers: createTOTPHeaders(totp), params: queryParams },
    );

    return data;
  }

  async replaceWhitelist(
    params: ReplaceWhitelistParams,
    body: ReplaceWhitelistBody,
    totp?: string,
  ) {
    const { data } = await this.api.post<ReplaceWhitelistResponse>(
      '/api/v1/auth/whitelist/replace',
      body,
      { headers: createTOTPHeaders(totp), params },
    );

    return data;
  }

  async getGoogleLoginParams() {
    const { data } = await this.api.get<IGoogleLoginParamsResponse>(
      '/api/v1/googleOauth/getGoogleOauthParams',
    );

    return data;
  }

  async loginUserByGoogleSecretCode(
    body: IOauthSecretCodeParams,
    totp?: string,
  ) {
    const { data } = await this.api.post<IOauthLoginResponse>(
      '/api/v1/googleOauth/loginUserByGoogleSecretCode',
      body,
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async bindGoogleAccount(body: IGoogleSecretCodeParams, totp?: string) {
    const { data } = await this.api.post<IOauthLoginResponse>(
      '/api/v1/auth/googleOauth/bindEmail',
      body,
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async getAssociatedAccounts() {
    const { data } = await this.api.get<AssociatedAccount[]>(
      '/api/v1/auth/abstractBindings/list',
    );

    return data;
  }

  async bindOauthAccount(body: IOauthSecretCodeParams, totp?: string) {
    const { data } = await this.api.post<IOauthLoginResponse>(
      '/api/v1/auth/abstractBindings/bind',
      body,
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async unbindOauthAccount(params: ISecretCodeLoginQueryParams, totp?: string) {
    const { data } = await this.api.post<IUnbindOauthAccountResponse>(
      '/api/v1/auth/abstractBindings/unbind',
      undefined,
      {
        params,
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async getETHAddresses() {
    const { data } = await this.api.get<IETHAddressesResponse>(
      '/api/v1/auth/googleOauth/getAllMyEthAddresses',
    );

    return data;
  }

  async decodeJwtToken(body: IDecodeJwtTokenParams) {
    const { data } = await this.api.post<IDecodeJwtTokenResponse>(
      '/api/v1/auth/googleOauth/decodeJwtToken',
      body,
    );

    return data;
  }

  async getSyntheticJwtToken(totp?: string) {
    const { data } = await this.api.get<ISyntheticJwtTokenResponse>(
      '/api/v1/auth/jwt/getMySyntheticJwt',
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async loginBySecretCode(
    body: IOauthSecretCodeParams,
    params: ISecretCodeLoginQueryParams,
    totp?: string,
  ) {
    const { data } = await this.api.post<IOauthLoginResponse>(
      '/api/v1/oauth2/loginBySecretCode',
      body,
      {
        params,
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async getOauthLoginParams({ provider }: IOauthLoginParams) {
    const { data } = await this.api.get<IGoogleLoginParamsResponse>(
      '/api/v1/oauth2/getProviderParams',
      { params: { provider } },
    );

    return data;
  }

  async getCryptoPaymentOptions(
    params: IGetCryptoPaymentOptionsParams | void = { active: true },
  ) {
    const { data } = await this.api.get<IGetCryptoPaymentOptionsResponse>(
      '/api/v1/auth/payment/getCryptoPaymentOptions',
      { params },
    );

    return data;
  }

  async updateGroupMemberRole({
    group,
    userAddress,
    role,
  }: IUpdateGroupMemberRoleParams) {
    const { data } = await this.api.patch<IUpdateGroupMemberRoleResponse>(
      '/api/v1/auth/groups/members',
      {
        user_address: userAddress,
        role,
      },
      {
        params: { group },
      },
    );

    return data;
  }

  async removeGroupTeammate({ group, address }: IDeleteGroupMemberParams) {
    const { data } = await this.api.delete<IUpdateGroupMemberRoleResponse>(
      '/api/v1/auth/groups/members',
      {
        params: { address, group },
      },
    );

    return data;
  }

  async getGroupsInvitations(params?: IGetGroupInvitationsParams) {
    const { data } = await this.api.get<IGetGroupInvitationsResponse>(
      '/api/v1/auth/invitations',
      {
        params: { statuses: params?.invitationsStatuses },
        paramsSerializer: request => stringify(request, { indices: false }),
      },
    );

    return data.invitations;
  }

  async cancelGroupInvitation({ group, email }: ICancelGroupInvitationParams) {
    const { data } = await this.api.post<IManageGroupInvitationResponse>(
      '/api/v1/auth/groups/invite/cancel',
      {
        email,
      },
      {
        params: { group },
      },
    );

    return data;
  }

  async resendGroupInvitation({ group, email }: ICancelGroupInvitationParams) {
    const { data } = await this.api.post<IManageGroupInvitationResponse>(
      '/api/v1/auth/groups/invite/resend',
      {
        email,
      },
      {
        params: { group },
      },
    );

    return data;
  }

  async leaveTeam({ group }: IApiUserGroupParams) {
    const { data } = await this.api.delete<ILeaveGroupResponse>(
      '/api/v1/auth/groups/leave',
      {
        params: { group },
      },
    );

    return data;
  }

  async inviteGroupMember({ group, invitations }: InviteGroupMemeberParams) {
    const {
      data: { result },
    } = await this.api.post<InivteGroupMemberResponse>(
      '/api/v1/auth/groups/invite',
      invitations,
      { params: { group } },
    );

    return result;
  }

  async renameTeam({ group, name, comment, company_type }: IRenameGroupParams) {
    const { data } = await this.api.patch<IRenameGroupResponse>(
      '/api/v1/auth/groups/detail',
      {
        name,
        comment,
        company_type,
      },
      {
        params: { group },
      },
    );

    return data;
  }

  async acceptGroupInvitation(body: AcceptGroupInvitationParams) {
    await this.api.post('/api/v1/auth/groups/invite/accept', body);
  }

  async rejectGroupInvitation(body: RejectGroupInvitationParams) {
    await this.api.post('/api/v1/auth/groups/invite/reject', body);
  }

  async getNotifications(params?: IGetNotificationsParams) {
    const { data: response } = await this.api.get<IGetNotificationsResponse>(
      '/api/v1/auth/notifications',
      {
        params,
      },
    );

    return response;
  }

  async markNotificationsAsRead({
    ids,
    group,
    seen,
  }: IMarkNotificationsAsReadParams) {
    const { data } = await this.api.patch<IMarkNotificationsAsReadResponse>(
      '/api/v1/auth/notifications/status',
      { ids, seen },
      { params: { group } },
    );

    return data;
  }

  async getNotificationsChannels(params?: IGetNotificationsChannelsParams) {
    const { data: response } = await this.api.get<
      IGetNotificationsChannelsResponse[]
    >('/api/v1/auth/notifications/channels', {
      params,
    });

    return response;
  }

  async updateNotificationsChannelStatus({
    active,
    channel,
    group,
  }: IUpdateNotificationsChannelStatusParams) {
    const { data: response } =
      await this.api.patch<IUpdateNotificationsChannelStatusResponse>(
        '/api/v1/auth/notifications/channels/status',
        { active, channel },
        {
          params: { group },
        },
      );

    return response;
  }

  async updateNotificationsChannel(params: IUpdateNotificationsChannelParams) {
    const { group, ...restParams } = params;

    const { data } = await this.api.post<INotificationsChannelConfig>(
      '/api/v1/auth/notifications/channels/config',
      restParams,
      { params: { group } },
    );

    return data;
  }

  async getTelegramNotificationsBotData() {
    const { data: response } =
      await this.api.get<IGetMessengerNotificationsBotDataResponse>(
        '/api/v1/auth/notifications/telegram/bot',
      );

    return response;
  }

  async enableTelegramIntegration({
    confirmation_data,
    group,
  }: IEnableTelegramIntegrationParams) {
    const { data } = await this.api.post<IEnableMessengerIntegrationResponse>(
      '/api/v1/auth/notifications/telegram/enable',
      { confirmation_data },
      {
        params: { group },
      },
    );

    return data;
  }

  async getSlackNotificationsBotData() {
    const { data: response } =
      await this.api.get<IGetMessengerNotificationsBotDataResponse>(
        '/api/v1/auth/notifications/slack/bot',
      );

    return response;
  }

  async enableSlackIntegration({ code, group }: IEnableSlackIntegrationParams) {
    const { data } = await this.api.post<IEnableMessengerIntegrationResponse>(
      '/api/v1/auth/notifications/slack/enable',
      { code },
      {
        params: {
          group,
        },
      },
    );

    return data;
  }

  async getSlackNotificationsBotDetails(
    params?: IGetMessengerNotificationsBotDetailsParams,
  ) {
    const { data: response } =
      await this.api.get<IGetMessengerNotificationsBotDetailsResponse>(
        '/api/v1/auth/notifications/slack/details',
        { params },
      );

    return response;
  }

  async changeEmailForNotifications({
    email,
    group,
  }: IAddNewEmailForNotificationsParams) {
    const { data } = await this.api.post<IEnableMessengerIntegrationResponse>(
      '/api/v1/auth/notifications/email/enable',
      { email },
      {
        params: { group },
      },
    );

    return data;
  }

  async confirmEmailForNotifications({
    confirmation_data,
    group,
  }: IConfirmEmailForNotificationsParams) {
    const { data } = await this.api.post<IEnableMessengerIntegrationResponse>(
      '/api/v1/auth/notifications/email/confirm',
      { confirmation_data },
      {
        params: { group },
      },
    );

    return data;
  }

  async deleteNotificationsChannel(params: IDeleteNotificationsChannelParams) {
    const { data } = await this.api.delete<IDeleteNotificationsChannelResponse>(
      '/api/v1/auth/notifications/channels',
      { params },
    );

    return data;
  }

  async applyReferralCode(params: IApplyReferralCodeParams) {
    const { data } = await this.api.post<IApplyReferralCodeResult>(
      '/api/v1/auth/referral/apply',
      undefined,
      { params },
    );

    return data;
  }

  async getReferrer(params?: IGetReferrerParams) {
    const { data } = await this.api.get<IGetReferrerResponse>(
      '/api/v1/auth/referral/referrer',
      { params },
    );

    return data.referrer;
  }

  async createReferralCode(params?: ICreateReferralCodeParams) {
    const { data } = await this.api.post<ICreateReferralCodeResult>(
      '/api/v1/auth/referral/create',
      { params },
    );

    return data.referralCode;
  }

  async getReferralCodes(params?: IGetReferralCodesParams) {
    const { data } = await this.api.get<IGetReferralCodesResult>(
      '/api/v1/auth/referral/codes',
      { params },
    );

    return data.referralCodes;
  }

  async getReferralLinksByCodes({
    codes: code,
    group,
  }: IGetReferralLinksByCodesParams) {
    const { data } = await this.api.get<IGetReferralLinkByCodesResult>(
      '/api/v1/auth/referral/urlsWithCode',
      {
        params: { code, group },
        paramsSerializer: params => stringify(params, { indices: false }),
      },
    );

    return data.referral_urls;
  }

  async getRewardBalance(params?: IGetRewardBalanceParams) {
    const { data } = await this.api.get<IGetRewardBalanceResult>(
      '/api/v1/auth/referral/reward/balance',
      { params },
    );

    return data;
  }

  async getReferralsCount(params?: IGetReferralsCountParams) {
    const { data } = await this.api.get<IGetReferralsCountResult>(
      '/api/v1/auth/referral/referrals/count',
      { params },
    );

    return data;
  }

  async convertReferralReward(body: IConvertReferralRewardParams) {
    await this.api.post('/api/v1/auth/referral/reward/convert', body);
  }

  async getRewardTxs(params?: IGetRewardTxsParams) {
    const {
      data: { transactions },
    } = await this.api.get<IGetRewardTxsResponse>(
      '/api/v1/auth/referral/reward/txs',
      { params },
    );

    return transactions;
  }

  /**
   * FileManager (Invoice documents)
   * https://ankrnetwork.atlassian.net/wiki/spaces/PBRPC/pages/2743992321/Invoices
   * */

  async checkOrGenerateCryptoDepositInvoice(
    params: TCheckOrGenerateDepositInvoiceParams,
  ) {
    const { data } = await this.api.get<ICheckOrGenerateDepositInvoiceResponse>(
      '/api/v1/auth/document/invoice/cryptoDeposit',
      { params },
    );

    return data;
  }

  async getCryptoDepositInvoiceDocument(params: IGetDepositInvoiceParams) {
    const { data } = await this.api.get<TGetDepositInvoiceResponse>(
      '/api/v1/auth/document/invoice/cryptoDeposit/download',
      { params, responseType: 'blob' },
    );

    /**
     * Response is either a file or an error
     * Headers:
     * Content-Type: "application/octet-stream"
     * Content-Disposition: "attachment; filename=invoice_web3api.pdf"
     */

    return data;
  }

  async getStripeDepositInvoiceLinks(params: IGetStripeInvoiceDocumentParams) {
    const { data } = await this.api.get<IGetStripeInvoiceDocumentResponse>(
      '/api/v1/auth/document/invoice/stripeDocuments',
      { params },
    );

    return data;
  }

  async createAPIKey(params: CreateAPIKeyParams, totp?: string) {
    const { data } = await this.api.post<CreateAPIKeyResponse>(
      '/api/v1/auth/token/custom/new',
      params,
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return data;
  }

  async getAPIKeys() {
    const { data } = await this.api.get<APIKey[]>(
      '/api/v1/auth/token/custom/all',
    );

    return data;
  }

  async deleteAPIKeys(body: DeleteAPIKeysParams, totp?: string) {
    const { data } = await this.api.post<DeleteAPIKeysResponse>(
      '/api/v1/auth/token/custom/delete',
      body,
      {
        headers: createTOTPHeaders(totp),
      },
    );

    return data.results;
  }

  async getUIProfileConfig(params?: IGetUIProfileConfigParams) {
    const { data } = await this.api.get<IUIProfileConfig>(
      '/api/v1/auth/users/profile',
      { params },
    );

    return data;
  }

  async updateUIProfileConfig(
    { config, group }: IUpdateUIProfileConfigParams,
    totp?: string,
  ) {
    const { data } = await this.api.post<IUpdateUIProfileConfigResponse>(
      '/api/v1/auth/users/config/ui',
      { config },
      {
        headers: createTOTPHeaders(totp),
        params: { group },
      },
    );

    return data;
  }

  async getAllSessions() {
    const { data } = await this.api.get<IGetAllSessionsResponse[]>(
      '/api/v1/auth/session/ui/all',
    );

    return data;
  }

  async deleteSessions(body: IDeleteSessionParams) {
    const { data } = await this.api.post<IDeleteSessionResponse>(
      '/api/v1/auth/session/ui/delete',
      body,
    );

    return data;
  }

  async logoutCurrentSession() {
    const { data } = await this.api.post<ILogoutCurrentSessionResponse>(
      '/api/v1/auth/session/ui/logout',
    );

    return data;
  }

  async createNewSession(body: ICreateNewSessionParams) {
    const { data } = await this.api.post<ICreateNewSessionResponse>(
      '/api/v1/auth/session/ui/new',
      body,
    );

    return data;
  }
}
